// src/components/trust/TrustCenterPage.jsx
// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.

import React, { useEffect, useState } from "react";

import LegalDocumentModal from "../legal/LegalDocumentModal";
import "./TrustCenterPage.css";

const POSTURE_ITEMS = [
  {
    label: "Connector access",
    value: "Read-only",
    tone: "success",
  },
  {
    label: "AI operating mode",
    value: "Grounded & advisory",
    tone: "success",
  },
  {
    label: "Execution authority",
    value: "Human controlled",
    tone: "success",
  },
  {
    label: "Legal & lifecycle policies",
    value: "Published",
    tone: "success",
  },
];

const SECURITY_CONTROLS = [
  {
    title: "Tenant-scoped authorization",
    status: "Operational",
    body:
      "Authenticated product requests are resolved within tenant and user context. OASYS keeps authorization decisions in the application control plane rather than relying on client-supplied identity alone.",
  },
  {
    title: "Read-only provider connections",
    status: "Operational",
    body:
      "Enterprise connector setup is designed for evidence collection through approved read-only permissions. OASYS connector controls disable cloud mutation and destructive actions for governed evidence collection paths.",
  },
  {
    title: "Protected connector credentials",
    status: "Operational",
    body:
      "Durable connector authorization material is encrypted before storage and decrypted only when required for authorized execution. Secrets are not intended for frontend configuration or customer-visible diagnostics.",
  },
  {
    title: "Governed audit trails",
    status: "Operational",
    body:
      "OASYS preserves operational history across evidence, validation, remediation, workflow, connector, and AI interaction surfaces so important state transitions remain reviewable.",
  },
];

const AI_GUARDRAILS = [
  "Deterministic OASYS records, validation engines, workflow state, and authorization state remain authoritative.",
  "OASYS Intelligence requires grounded context for governed reasoning and does not treat model confidence as permission to close, validate, or execute work.",
  "Recommendations remain advisory. Operators retain authority over provider authorization, remediation execution, approvals, validation, and closure.",
  "Model requests are bounded by governed timeout, output, and context budgets, with provider failures routed to deterministic fallback behavior.",
];

const DATA_HANDLING = [
  {
    title: "Evidence collection",
    body:
      "OASYS collects source-system signals through the provider authorization and evidence scope approved for the connector. The platform does not require write access for governed evidence collection.",
  },
  {
    title: "Historical governance records",
    body:
      "Certain evidence, audit, workflow, remediation, and document-control records are intentionally preserved to support traceability and controlled history. Retention behavior can differ by record type and governing requirement.",
  },
  {
    title: "Customer lifecycle policy",
    body:
      "The published OASYS Data Lifecycle Policy describes standard retention, lifecycle holds, offboarding, ordinary tenant purge, governed archive, backup, and completion-record practices without treating every data category as subject to one universal retention period.",
  },
];

const POLICY_ITEMS = [
  {
    title: "Privacy Policy",
    status: "Published",
    tone: "success",
    body: "Versioned customer-facing privacy terms describe how OASYS processes personal information and Customer Data.",
    documentKey: "privacy",
    actionLabel: "View Privacy Policy",
  },
  {
    title: "Customer Terms",
    status: "Published",
    tone: "success",
    body: "Baseline enterprise SaaS terms define access rights, Customer responsibilities, OASYS boundaries, and commercial-use conditions.",
    documentKey: "terms",
    actionLabel: "View Customer Terms",
  },
  {
    title: "Data Lifecycle",
    status: "Published",
    tone: "success",
    body: "Versioned customer-facing policy describes standard retention, lifecycle holds, offboarding, deletion boundaries, governed archive, backup lifecycle, and completion records.",
    documentKey: "lifecycle",
    actionLabel: "View Data Lifecycle Policy",
  },
];

const CONTACT_ITEMS = [
  {
    title: "Customer Support",
    status: "Available",
    tone: "success",
    email: "support@oasyshq.com",
    actionLabel: "Email support",
    href: "mailto:support@oasyshq.com?subject=OASYS%20Support%20Request",
    body: "Use this channel for product questions, account assistance, and general support. Response commitments follow the applicable plan, Order Form, or separately agreed support terms.",
    guidance: "Include your organization, a concise description of the issue, and any relevant request or correlation ID when available.",
  },
  {
    title: "Security Reporting",
    status: "Dedicated channel",
    tone: "success",
    email: "security@oasyshq.com",
    actionLabel: "Report security concern",
    href: "mailto:security@oasyshq.com?subject=OASYS%20Security%20Report",
    body: "Report suspected vulnerabilities or security concerns privately through the dedicated OASYS security channel.",
    guidance: "Share enough detail to help us understand the affected service and impact, but do not send passwords, API keys, access tokens, or other secrets.",
  },
];

const ASSURANCE_METRICS = [
  {
    label: "Release control",
    value: "Versioned & verified",
  },
  {
    label: "Provider health",
    value: "Observed safely",
  },
  {
    label: "Fallback behavior",
    value: "Deterministic",
  },
];

function StatusChip({ children, tone = "informational" }) {
  return (
    <span className={`oasys-status-chip oasys-status-chip--${tone}`}>
      {children}
    </span>
  );
}

function SectionHeader({ eyebrow, title, description, id }) {
  return (
    <div className="oasys-section__header">
      <div className="oasys-section__heading">
        <span className="oasys-section__eyebrow">{eyebrow}</span>
        <h2 className="oasys-section__title" id={id}>
          {title}
        </h2>
        {description ? (
          <p className="oasys-section__description">{description}</p>
        ) : null}
      </div>
    </div>
  );
}

export default function TrustCenterPage({
  apiBaseUrl,
  getAccessToken,
  onModeChange,
}) {
  const [legalModalKey, setLegalModalKey] = useState(null);
  const [accessState, setAccessState] = useState("loading");
  const [accessMessage, setAccessMessage] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    const resolveTrustCenterAccess = async () => {
      setAccessState("loading");
      setAccessMessage("");

      try {
        if (!apiBaseUrl || typeof getAccessToken !== "function") {
          throw new Error("Trust Center access authority is unavailable.");
        }

        const token = await getAccessToken();
        if (!token || !active) return;

        const response = await fetch(`${apiBaseUrl}/trust-center/access`, {
          method: "GET",
          signal: controller.signal,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const responseText = await response.text();
        let payload = {};

        if (responseText) {
          try {
            payload = JSON.parse(responseText);
          } catch {
            payload = { detail: responseText };
          }
        }

        if (!response.ok) {
          const detail = payload?.detail;
          const message =
            typeof detail === "string"
              ? detail
              : detail?.message
              ? detail.message
              : response.status === 403
              ? "Your current OASYS package does not include Trust Center access."
              : `Trust Center access check failed (${response.status}).`;

          if (active) {
            setAccessState("denied");
            setAccessMessage(message);
          }
          return;
        }

        if (
          payload?.access !== "granted" ||
          payload?.entitlement !== "trust.center"
        ) {
          throw new Error("Trust Center admission returned an invalid response.");
        }

        if (active) {
          setAccessState("granted");
          setAccessMessage("");
        }
      } catch (error) {
        if (error?.name === "AbortError" || !active) return;

        setAccessState("denied");
        setAccessMessage(
          String(error?.message || "Trust Center access could not be verified.")
        );
      }
    };

    resolveTrustCenterAccess();

    return () => {
      active = false;
      controller.abort();
    };
  }, [apiBaseUrl, getAccessToken]);

  if (accessState !== "granted") {
    return (
      <div className="oasys-page-canvas trust-center-page">
        <div className="oasys-page-shell trust-center-shell">
          <section className="oasys-section oasys-section--comfortable">
            <div className="oasys-section__header">
              <div className="oasys-section__heading">
                <span className="oasys-section__eyebrow">OASYS Trust Center</span>
                <h1 className="oasys-page-header__title">
                  {accessState === "loading"
                    ? "Verifying Trust Center access..."
                    : "Trust Center access unavailable"}
                </h1>
                {accessState === "denied" ? (
                  <p className="oasys-section__description">
                    {accessMessage || "Access could not be verified."}
                  </p>
                ) : null}
              </div>
            </div>
          </section>
        </div>
      </div>
    );
  }

  const goTo = (mode, params) => {
    if (typeof onModeChange === "function") onModeChange(mode, params);
  };

  const openLegalPage = (mode) => {
    setLegalModalKey(null);
    goTo(mode);
  };

  return (
    <div className="oasys-page-canvas trust-center-page">
      <div className="oasys-page-shell trust-center-shell">
        <header className="oasys-page-header trust-center-header">
          <div className="oasys-page-header__content">
            <div className="trust-center-title-row">
              <div>
                <div className="oasys-eyebrow">OASYS Trust Center</div>
                <h1 className="oasys-page-header__title" id="trust-center-title">
                  Security, governance, and responsible AI by design.
                </h1>
              </div>
              <StatusChip tone="success">Production controls active</StatusChip>
            </div>
            <p className="oasys-page-header__description trust-center-header__description">
              This page summarizes the production controls OASYS can substantiate today. It is
              intentionally conservative: operational controls are described as implemented,
              while customer policies that are still being finalized are marked accordingly.
            </p>
          </div>
        </header>

        <section className="trust-overview-grid" aria-labelledby="trust-center-title">
          <div className="oasys-section oasys-section--comfortable trust-overview-card">
            <SectionHeader
              eyebrow="Trust posture"
              title="Production safeguards are visible, bounded, and reviewable."
              description="OASYS separates implemented operational controls from customer-facing policy commitments that are still progressing through approval and publication."
            />
            <div className="oasys-action-group oasys-action-group--start">
              <button
                type="button"
                className="oasys-button oasys-button--medium oasys-button--primary"
                onClick={() => goTo("administration", { section: "connectors" })}
              >
                <span className="oasys-button__label">Review connector controls</span>
              </button>
              <button
                type="button"
                className="oasys-button oasys-button--medium oasys-button--secondary"
                onClick={() => goTo("evidence")}
              >
                <span className="oasys-button__label">Open evidence &amp; audit</span>
              </button>
            </div>
          </div>

          <aside className="oasys-card oasys-card--comfortable trust-posture-card" aria-label="Current trust posture">
            <div className="trust-posture-card__header">
              <div>
                <span className="oasys-section__eyebrow">Current posture</span>
                <h2 className="trust-posture-card__title">Production</h2>
              </div>
            </div>
            <div className="trust-posture-list">
              {POSTURE_ITEMS.map((item) => (
                <div className="trust-posture-row" key={item.label}>
                  <span className="trust-posture-row__label">{item.label}</span>
                  <StatusChip tone={item.tone}>{item.value}</StatusChip>
                </div>
              ))}
            </div>
          </aside>
        </section>

        <section
          className="oasys-section oasys-section--comfortable trust-section"
          aria-labelledby="trust-security-title"
        >
          <SectionHeader
            eyebrow="Security architecture"
            id="trust-security-title"
            title="Controls built around constrained access and traceability."
            description="OASYS is designed to collect compliance evidence without turning the compliance platform into an unrestricted change engine."
          />

          <div className="trust-card-grid trust-card-grid--two">
            {SECURITY_CONTROLS.map((control) => (
              <article className="oasys-card oasys-card--default trust-control-card" key={control.title}>
                <div className="trust-control-card__topline">
                  <span className="trust-control-card__indicator" aria-hidden="true">✓</span>
                  <StatusChip tone="success">{control.status}</StatusChip>
                </div>
                <h3>{control.title}</h3>
                <p>{control.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="trust-ai-grid" aria-label="AI governance and human authority">
          <article className="oasys-section oasys-section--comfortable trust-ai-card" aria-labelledby="trust-ai-title">
            <SectionHeader
              eyebrow="OASYS Intelligence"
              id="trust-ai-title"
              title="AI assists reasoning. It does not replace operational authority."
              description="OASYS Intelligence is a governed advisory layer over deterministic OASYS state. Grounding, provenance, human approval, and deterministic fallback remain part of the production operating model."
            />

            <ul className="trust-check-list">
              {AI_GUARDRAILS.map((item) => (
                <li key={item}>
                  <span className="trust-check-list__icon" aria-hidden="true">✓</span>
                  <p>{item}</p>
                </li>
              ))}
            </ul>
          </article>

          <article className="oasys-section oasys-section--comfortable" aria-labelledby="trust-human-title">
            <SectionHeader
              eyebrow="Human authority boundary"
              id="trust-human-title"
              title="Recommendations stop where governed decisions begin."
            />

            <div className="trust-boundary-list">
              <div className="oasys-card oasys-card--secondary oasys-card--default trust-boundary-card">
                <span>AI may</span>
                <strong>Explain, summarize, prioritize, and recommend.</strong>
              </div>
              <div className="oasys-card oasys-card--secondary oasys-card--default trust-boundary-card">
                <span>OASYS controls</span>
                <strong>Permissions, deterministic validation, workflow state, and closure gates.</strong>
              </div>
              <div className="oasys-card oasys-card--secondary oasys-card--default trust-boundary-card">
                <span>Operators retain</span>
                <strong>Authorization, approval, remediation, validation, and closure decisions.</strong>
              </div>
            </div>
          </article>
        </section>

        <section
          className="oasys-section oasys-section--comfortable trust-section"
          aria-labelledby="trust-data-title"
        >
          <SectionHeader
            eyebrow="Data handling"
            id="trust-data-title"
            title="Collection is scoped. Lifecycle commitments stay explicit."
            description="OASYS distinguishes technical retention behavior from customer-facing contractual retention promises. The latter are published only after policy approval."
          />

          <div className="trust-card-grid trust-card-grid--three">
            {DATA_HANDLING.map((item) => (
              <article className="oasys-card oasys-card--default trust-copy-card" key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section
          className="oasys-section oasys-section--comfortable trust-section"
          aria-labelledby="trust-assurance-title"
        >
          <SectionHeader
            eyebrow="Operational assurance"
            id="trust-assurance-title"
            title="Production change control is treated as a governed system."
            description="OASYS production readiness uses explicit release identity, migration authority, health/readiness checks, rollback procedures, runtime telemetry, and regression acceptance rather than relying on informal deployment confidence."
          />

          <div className="trust-metric-grid" aria-label="Operational assurance practices">
            {ASSURANCE_METRICS.map((metric) => (
              <div className="oasys-metric oasys-metric--card oasys-metric--compact" key={metric.label}>
                <span className="oasys-metric__label">{metric.label}</span>
                <strong className="trust-metric-value">{metric.value}</strong>
              </div>
            ))}
          </div>
        </section>

        <section className="oasys-alert oasys-alert--neutral trust-claims-alert" aria-labelledby="trust-claims-title">
          <div className="oasys-alert__icon" aria-hidden="true">i</div>
          <div className="oasys-alert__content">
            <h2 className="oasys-alert__title" id="trust-claims-title">
              Framework support is not a certification claim.
            </h2>
            <p className="oasys-alert__message">
              OASYS can evaluate, organize, monitor, and report against supported compliance
              frameworks and control sets. Product support for a framework does not, by itself,
              mean OASYS is certified to that framework or that a customer has achieved
              certification.
            </p>
          </div>
        </section>

        <section
          className="oasys-section oasys-section--comfortable trust-section"
          aria-labelledby="trust-policy-title"
        >
          <SectionHeader
            eyebrow="Policies"
            id="trust-policy-title"
            title="Published customer policies"
            description="Published legal and lifecycle documents are versioned and available directly from the Trust Center."
          />

          <div className="trust-card-grid trust-card-grid--two">
            {POLICY_ITEMS.map((item) => (
              <article className="oasys-card oasys-card--default trust-policy-card" key={item.title}>
                <div className="trust-policy-card__header">
                  <h3>{item.title}</h3>
                  <StatusChip tone={item.tone || "informational"}>{item.status}</StatusChip>
                </div>
                <p>{item.body}</p>

                {item.documentKey ? (
                  <div className="oasys-action-group oasys-action-group--start trust-policy-card__actions">
                    <button
                      type="button"
                      className="oasys-button oasys-button--medium oasys-button--secondary"
                      onClick={() => setLegalModalKey(item.documentKey)}
                    >
                      <span className="oasys-button__label">{item.actionLabel}</span>
                    </button>
                  </div>
                ) : null}
              </article>
            ))}
          </div>
        </section>

        <section
          className="oasys-section oasys-section--comfortable trust-section"
          aria-labelledby="trust-contact-title"
        >
          <SectionHeader
            eyebrow="Support & security"
            id="trust-contact-title"
            title="Dedicated channels for help and responsible reporting."
            description="Use customer support for product and account questions. Use the security channel for suspected vulnerabilities or security concerns."
          />

          <div className="trust-card-grid trust-card-grid--two">
            {CONTACT_ITEMS.map((item) => (
              <article className="oasys-card oasys-card--default trust-contact-card" key={item.title}>
                <div className="trust-contact-card__header">
                  <h3>{item.title}</h3>
                  <StatusChip tone={item.tone}>{item.status}</StatusChip>
                </div>
                <p>{item.body}</p>
                <a className="trust-contact-card__email" href={`mailto:${item.email}`}>
                  {item.email}
                </a>
                <p className="trust-contact-card__guidance">{item.guidance}</p>
                <div className="oasys-action-group oasys-action-group--start trust-contact-card__actions">
                  <a
                    className="oasys-button oasys-button--medium oasys-button--secondary"
                    href={item.href}
                  >
                    <span className="oasys-button__label">{item.actionLabel}</span>
                  </a>
                </div>
              </article>
            ))}
          </div>
        </section>

        <footer className="trust-footer-note">
          <strong>Trust Center principle:</strong>
          <span>
            OASYS publishes what the production system and approved operating policies can support
            — not aspirational certifications or controls that have not been accepted.
          </span>
        </footer>
      </div>

      <LegalDocumentModal
        documentKey={legalModalKey}
        open={Boolean(legalModalKey)}
        onClose={() => setLegalModalKey(null)}
        onOpenFullPage={openLegalPage}
      />
    </div>
  );
}
